import { Button as UIButton } from "@shared/components/untitledui/base/buttons/button"
import { Dropdown } from "@shared/components/untitledui/base/dropdown/dropdown"
import { cx } from "@shared/components/untitledui/util/cx"
import type { Course, CourseCalendarTime } from "@shared/models/course"
import type { CourseDateCombined } from "@shared/models/course-date-combined"
import { tracked } from "@supergrain/kernel/react"
import {
  formatCourseTime,
  formatCourseTimePart,
  parseCourseTimePart,
} from "cc-frontend/utils/course/times"
import { isEqual } from "lodash-es"
import { useState } from "react"
import { Button as AriaButton, Dialog as AriaDialog, DialogTrigger } from "react-aria-components"

interface Props {
  courseDate: CourseDateCombined
  course: Course
  /** Persists the custom time; `null` clears it back to the course default. */
  setCustomTime: (time: CourseCalendarTime | null) => void
}

/**
 * The lesson's course time, if present. Shown in the title bar and edited in a popover.
 */
export default tracked(function ChangeTime({ courseDate, course, setCustomTime }: Props) {
  // No time to show unless a default time exists for this date.
  if (courseDate.timeDefault === null) return null
  // A class that isn't meeting has no hour: printing one under a day marked off reads as if it
  // still met. The time comes back with the class when the day is turned back on.
  if (courseDate.isOff) return null

  const displayed = courseDate.timeCustom ?? courseDate.timeDefault
  const canEdit = course.meta?.permissions?.includes("can-edit-plan") ?? false

  const submit = (time: CourseCalendarTime) => {
    // Reset to the default (null) when the entered time equals the default.
    setCustomTime(isEqual(time, courseDate.timeDefault) ? null : time)
  }

  return (
    <DialogTrigger>
      <AriaButton
        data-test="lesson time"
        aria-label="Change time"
        className={cx(
          "-mx-1 w-fit max-w-full cursor-pointer overflow-hidden rounded px-1 text-left text-[0.9375rem] leading-none text-ellipsis whitespace-nowrap outline-hidden select-none",
          "hover:underline focus-visible:outline-2 focus-visible:outline-current",
        )}
      >
        <div className="select-none" data-test-lesson-time>
          {formatCourseTime(displayed)}
        </div>
      </AriaButton>
      <Dropdown.Popover placement="bottom left" arrow className="w-72 shadow-menu">
        <AriaDialog aria-label="Change time" className="flex flex-col outline-hidden">
          {({ close }) => (
            <>
              {/* Header — matches the lesson menu's popover header (see LessonMenu). */}
              <div className="border-b border-border-secondary px-5 py-2.5 text-left">
                <h3 className="m-0 menu-header">Change Time</h3>
              </div>
              <ChangeTimeBody
                initial={displayed}
                canEdit={canEdit}
                onSubmit={(time) => {
                  submit(time)
                  close()
                }}
              />
            </>
          )}
        </AriaDialog>
      </Dropdown.Popover>
    </DialogTrigger>
  )
})

/** The Start/End editing form inside the popover. */
function ChangeTimeBody({
  initial,
  canEdit,
  onSubmit,
}: {
  initial: CourseCalendarTime
  canEdit: boolean
  onSubmit: (time: CourseCalendarTime) => void
}) {
  const [startTime, setStartTime] = useState(initial.start)
  const [endTime, setEndTime] = useState(initial.end)

  const commitStart = (input: string) => {
    const start = parseCourseTimePart(input)
    if (start !== null) setStartTime(start)
  }
  const commitEnd = (input: string) => {
    // Passing the current start lets the parser enforce start <= end and guess AM/PM.
    const end = parseCourseTimePart(input, startTime)
    if (end !== null) setEndTime(end)
  }

  const submit = () => {
    // We enforce start <= end when setting end but not when setting start.
    const start = Math.min(startTime, endTime)
    onSubmit({ start, end: endTime })
  }

  return (
    <div className="flex w-full flex-col gap-3 p-3" data-test="lesson change time body">
      <div className="flex flex-row gap-3">
        <TimePartInput
          label="Start"
          value={startTime}
          isEditable={canEdit}
          placeholder="e.g. 9:00am"
          onCommit={commitStart}
          dataTest="lesson change time start"
        />
        <TimePartInput
          label="End"
          value={endTime}
          isEditable={canEdit}
          placeholder="e.g. 10:00am"
          onCommit={commitEnd}
          dataTest="lesson change time end"
        />
      </div>
      {canEdit && (
        <UIButton
          data-test="lesson change time submit"
          color="primary"
          size="md"
          className="w-full"
          onPress={submit}
        >
          Submit
        </UIButton>
      )}
    </div>
  )
}

/**
 * A single time part (Start or End) as a labeled, borderless text field. While focused it holds
 * the raw draft and commits it to the parent on every keystroke, so the parent's parsed value
 * stays current for an immediate Submit. On blur the draft clears and the field falls back to
 * the reformatted canonical `value` — reverting an unparseable entry and tidying a valid one.
 */
function TimePartInput({
  label,
  value,
  isEditable,
  placeholder,
  onCommit,
  dataTest,
}: {
  label: string
  value: number
  isEditable: boolean
  placeholder: string
  onCommit: (input: string) => void
  dataTest: string
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const display = draft ?? formatCourseTimePart(value)

  return (
    <label className="flex w-full flex-col rounded-md border border-link-blue px-1.5 py-1.5">
      <span className="text-[0.9375rem] text-link-blue">{label}</span>
      <input
        data-test={dataTest}
        readOnly={!isEditable}
        placeholder={placeholder}
        value={display}
        onChange={(event) => {
          setDraft(event.target.value)
          onCommit(event.target.value)
        }}
        onBlur={() => setDraft(null)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault()
            event.currentTarget.blur()
          }
        }}
        className="w-full content-center text-lg font-semibold outline-hidden"
      />
    </label>
  )
}
